import {
  all,
  cancel,
  fork,
  put,
  race,
  select,
  take,
} from 'typed-redux-saga/macro';
import { modules } from '@veritone/glc-redux';
import {
  ROUTE_DOCUMENT_DETAILS,
  selectCurrentRoutePayload,
} from '@common-modules/routing';
import watermarksSaga from '@redact-modules/watermarks/sagas';
import { actionFetchWatermarks } from '@redact-modules/watermarks/actions';
import { OPEN_DOCUMENT, OPEN_DOCUMENT_SUCCESS } from '../actions';
import { TDOId } from '@common-modules/universal/models/Brands';
import { actionFetchRedactionStatuses } from '@redact-modules/redactionStatuses/actions';
import { actionCheckTdoPermissions } from '@common-modules/mediaDetails/actions/view';
// Deep import (not the statusHistory barrel): the barrel pulls the sagas,
// which import this module's actions — a cycle (VE-26730).
import { actionFetchLatestStatusRow } from '@common-modules/statusHistory/actions';
import {
  waitForRedactionStatusesSettled,
  watchRedactionStatusSettings,
} from '@redact-modules/redactionStatuses/sagas';
import {
  resolveEditorReturnTarget,
  warmEditorReturnTargetTree,
} from '@redact-modules/casemedia/sagas/caseDiscovery';
import { onFetchLexicons } from '@redact-modules/lexicons/sagas';
import { lexiconActions } from '@redact-modules/lexicons';

const {
  user: { LOGOUT },
} = modules;

/**
 * Route entry-point saga registered on ROUTE_DOCUMENT_DETAILS in routes.ts.
 *
 * The routing system forks this when the route mounts and cancels it when the
 * route exits. On entry we:
 * 1. Read the tdoId from the route payload and resolve the file's parent
 *    request (VE-25873 — injecting it into a case-less URL, which redirects
 *    and re-runs this saga; we stop here in that case, before doing any work
 *    the re-run would repeat).
 * 2. Start the folder-view warm-up for the exit, plus the redaction-status
 *    watchers + fetch (same as initMediaDetailsPage) so a direct URL into the
 *    document editor still loads custom-status SDOs — needed for display
 *    labels and custom lock-edit policy. Also kick off the file's OLP check, so
 *    a caller without AIWARE_TDO_UPDATE gets a read-only editor rather than an
 *    edit-capable one (VE-20286), and boot the watermark library saga + fetch
 *    (VE-24693) — this route doesn't run initMainPage, so the editor's
 *    watermark panel would otherwise have no library to offer.
 * 3. Wait for that fetch to settle before OPEN_DOCUMENT, so lock-edit policy is
 *    evaluated against the org's real custom statuses rather than an empty
 *    list. If the fetch fails or times out, isRedactionStatusLockEdits falls
 *    back to failing *closed* on unrecognized statuses — the editor still
 *    opens, but a custom-status UUID is treated as locked rather than free.
 * 4. Dispatch OPEN_DOCUMENT — the documentsSaga (always-on) handles the
 *    actual load.
 * 5. Once the document has actually OPENED, kick off the tier-1 status-history
 *    read (VE-26730). Gated on the open rather than fired alongside it, because
 *    history rows carry correction-note text and the SDO read is authorized at
 *    schema level, not per TDO — see the comment at the dispatch.
 */
export function* initDocumentEditor() {
  // NOTE: don't combine `as any` with `yield* select(...)` on the same line —
  // a postfix TS cast around the whole `yield*` expression prevents the
  // typed-redux-saga/macro from transforming it into a single `yield`, so it
  // falls through to a generator-delegate that tries to iterate the effect
  // descriptor and throws "object is not iterable" at runtime.
  const payload = yield* select(selectCurrentRoutePayload);
  const tdoId = (payload as { tdoId?: TDOId } | undefined)?.tdoId;
  if (!tdoId) {
    return;
  }
  const redirected = yield* resolveEditorReturnTarget(ROUTE_DOCUMENT_DETAILS);
  if (redirected) {
    return;
  }

  // Attached forks — cancelled on LOGOUT below, or with this saga on route exit.
  const tasks = yield* all([
    fork(warmEditorReturnTargetTree),
    fork(watchRedactionStatusSettings),
    // The FETCH watcher only (VE-24948) — the lexicon ADMIN watchers stay a
    // settings-route concern. Forked here for the same reason as the statuses
    // watcher above: the editor's LexiconPanel lists lexicons, and a direct
    // URL into the editor never runs initMainPage, so without this its
    // FETCH_LEXICONS dispatch would land on no listener and the panel would
    // show whatever the store happened to hold.
    fork(onFetchLexicons),
    // Watermark library (VE-24693) — this route doesn't run initMainPage, so the
    // library saga has to be started here for the editor's watermark panel.
    fork(watermarksSaga),
  ]);

  // Fire-and-forget like the OLP check below: the panel renders an empty library
  // until it lands, so it must not delay OPEN_DOCUMENT.
  // `pickableOnly`: the editor reaches the library only through
  // `selectPickableWatermarks`, so a deleted or disabled row would be fetched,
  // decoded and discarded — artwork included.
  yield* put(
    actionFetchWatermarks({ limit: 1000, offset: 0, pickableOnly: true })
  );
  /**
   * THE LEXICON LIBRARY HAS TO BE FETCHED FROM HERE, not from whichever
   * component happens to want it (VE-24948).
   *
   * The editor HIDES its lexicon controls when the org's library holds nothing
   * applicable and nothing is applied to this document — a labelled button
   * whose panel says only that it is empty is worse than no button. That makes
   * the library an input to a rendering decision, and a fetch that rode on the
   * rendering closed the loop: nothing fetched, so the library read as empty,
   * so the control was not rendered, so it never mounted, so nothing fetched.
   * The control simply never appeared on a document with no lexicon already
   * applied.
   *
   * A component's mount effect cannot fix that, because the component whose
   * mount it is may be the thing being gated. The route can: it runs whatever
   * the panel decides. Fire-and-forget, like the watermark library above.
   */
  yield* put(lexiconActions.FETCH_LEXICONS());

  // VE-20286: resolve the file's OLP verdict so the document editor can open
  // read-only for a caller without AIWARE_TDO_UPDATE. The video editor asks for
  // this from the MediaDetails page; the document route has no equivalent
  // component-level hook, so it's dispatched here. The watcher
  // (watchCheckTdoPermissions) lives in the always-on initMediaDetails saga, so
  // it is listening regardless of which editor route is mounted, and the verdict
  // lands in the same slice selectRolePermissionHelper reads. Fire-and-forget:
  // the check defaults permissive on failure, so it must not delay the open.
  yield* put(actionCheckTdoPermissions({ tdoId }));

  yield* put(actionFetchRedactionStatuses());
  // Block OPEN_DOCUMENT until customs are in store (or the fetch failed) so
  // lock policy is evaluated against real SDOs, not an empty customStatuses
  // list. Time-bounded, so a hung fetch delays the open rather than blocking
  // it forever.
  yield* waitForRedactionStatusesSettled();

  yield* put(OPEN_DOCUMENT({ tdoId }));

  // VE-26730: tier-1 status-history read — the newest row drives the
  // correction-note pop-up/icon. Best-effort and gate-aware; never blocks open.
  //
  // TIMING gate only, not authorization: OPEN_DOCUMENT_SUCCESS is NOT proof of
  // read access — openDocument dispatches it with the client-echoed tdoId even
  // when the authorization-bearing media-info fetch failed (that failure is
  // deliberately swallowed so the editor degrades instead of blocking). The
  // authorization control lives in the statusHistory read funnel: openDocument
  // marks the TDO read-verified (actionMarkTdoReadVerified) only after the
  // media-info fetch returned server data for this caller, and the funnel drops
  // reads for unverified TDOs. This take just picks the right moment to ask.
  // (The OLP check above answers AIWARE_TDO_UPDATE — write access — so it is
  // the wrong gate: a legitimate read-only viewer must still see notes.)
  const opened = yield* race({
    success: take(
      (a: { type: string; payload?: { tdoId?: unknown } }) =>
        a.type === OPEN_DOCUMENT_SUCCESS.type &&
        String(a.payload?.tdoId) === String(tdoId)
    ),
    loggedOut: take(LOGOUT),
  });
  if (opened.success) {
    yield* put(actionFetchLatestStatusRow({ tdoId }));
    yield* take(LOGOUT);
  }

  yield* cancel(tasks);
}
